import type { Ref } from 'vue'
import * as constants from '../constants'
import type { Toast } from '../types'
import {
  AABBvsAABB,
  clamp,
  findPolygonIntersectionPoints2D,
  getAABBPoints,
  SAT,
  type SATResult,
  type SimpleContact
} from 'math'
import { getToastBound, toastAddPseudoImpulse } from './utils'
import type { UseToastsState } from '.'
const WITH_CONTACTS = false
const SKIP_Y = -(constants.TOAST_MIN_HEIGHT * 2)
const EPSILON = 0.00025

type Intersection = {
  a: Ref<Toast>
  b: Ref<Toast>
  data: SATResult
  factor: number
  contact?: SimpleContact
}

export const canIntersect = (toast: Toast): boolean => {
  if (toast.velocity.y > EPSILON) return true
  return toast.position.y > SKIP_Y
}

const respond = (inter: Intersection, dt: number) => {
  const {
    a,
    b,
    data: { normal, depth }
  } = inter
  const sepVel = b.value.velocity.sub(a.value.velocity)
  const dot = sepVel.dot(normal)

  if (dot > 0.000000005) return

  const invDt = 1.0 / Math.max(0.00001, dt)

  const same = b.value.trash === a.value.trash

  const resti = clamp((0.0033 + 0.0025 * (depth * depth)) * inter.factor, 0.0, 0.09)
  const j = -(1 + resti) * dot * inter.factor
  const impulse = normal.scale(j)
  const impulseA = impulse.scale(-1)
  const impulseB = impulse.scale(1)
  const pseudo = normal.scale(depth * 0.0015 * invDt)

  if (b.value.trash === false || same) {
    a.value.velocity = a.value.velocity.add(impulseA.scale(a.value.invMass))
    toastAddPseudoImpulse(a, pseudo.scale(-1))
  }

  if (a.value.trash === false || same) {
    b.value.velocity = b.value.velocity.add(impulseB.scale(b.value.invMass))
    toastAddPseudoImpulse(b, pseudo.scale(1))
  }
}

const correct = (inter: Intersection) => {
  const { a, b } = inter

  const same = b.value.trash === a.value.trash
  const canMoveA = b.value.trash === false || same
  const canMoveB = a.value.trash === false || same

  const correct = inter.data.normal
    .scale(Math.max(0.0, inter.data.depth - 0.0003))
    .scale(canMoveA && canMoveB ? 0.5 : 1.0)
  const correctA = correct.scale(-1)
  const correctB = correct.scale(1)

  if (canMoveA) {
    a.value.position = a.value.position.add(correctA)
  }

  if (canMoveB) {
    b.value.position = b.value.position.add(correctB)
  }
}

const handleIntersection = (inter: Intersection, dt: number) => {
  respond(inter, dt)
  correct(inter)
}

export const getIntersections = (toast: Ref<Toast>, state: UseToastsState): Intersection[] => {
  if (!canIntersect(toast.value)) return []
  const boundsA = getToastBound(toast.value)
  const pointsA = getAABBPoints(boundsA)
  const intersections: Intersection[] = []
  for (let i = 0; i < state.toasts.length; i++) {
    const next = state.toasts[i]
    if (
      !next ||
      !next.value ||
      next.value.uid === toast.value.uid ||
      next.value.height <= 0 ||
      !canIntersect(next.value)
    )
      continue
    const boundsB = getToastBound(next.value)
    const pointsB = getAABBPoints(boundsB)
    if (!AABBvsAABB(boundsA, boundsB)) continue
    const s = SAT(pointsA, pointsB)
    if (!s) continue

    let numContacts = 0
    if (WITH_CONTACTS) {
      const contacts = findPolygonIntersectionPoints2D(pointsA, pointsB)
      numContacts = contacts.length
      contacts.forEach((c) => {
        intersections.push({
          a: toast,
          b: next,
          data: {
            ...s,
            normal: c.normal
          },
          contact: c,
          factor: 1.0 / Math.max(1, contacts.length + 1)
        })
      })
    }

    intersections.push({
      a: toast,
      b: next,
      data: s,
      factor: WITH_CONTACTS ? 1.0 / Math.max(1, numContacts + 1) : 1.0
    })
  }

  return intersections
}

export const checkIntersections = (toast: Ref<Toast>, state: UseToastsState, dt: number) => {
  const intersections = getIntersections(toast, state)
  intersections.forEach((inter) => handleIntersection(inter, dt))
}
